// app/src/lib/cashflow/payroll.js — manual payroll event CRUD for forecast input.
//
// Also: persistent check-number ranges so we can spot payroll checks in the
// bank feed. Restaurants are encouraged to keep one or more checkbooks
// exclusively for payroll (e.g. #50000-#50999); each book is a `{from, to,
// label}` range stored on the userProfile row. A bank tx is a payroll check
// iff its description contains "CHECK #N" with N inside ANY range.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { cents, isoToday } from './util.js';
import { mutate } from '../scale/instrument.js';
import { currentClaims } from '../auth/cognitoClient.js';

/** @typedef {{ payrollId:string, userId:string, payDate:string, amountCents:number, label:string, staffCount?:number, notes?:string }} PayrollEvent */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export async function create(input) {
  const userId = await requireUserId();
  if (!input.payDate || !ISO_DATE_RE.test(input.payDate)) throw new Error('payroll: payDate must be YYYY-MM-DD');
  if (!Number.isFinite(input.amount) || input.amount <= 0) throw new Error('payroll: amount > 0 required');
  const row = {
    payrollId: input.payrollId || crypto.randomUUID(),
    userId,
    payDate: input.payDate,
    amountCents: cents(input.amount),
    label: input.label || 'Payroll',
    staffCount: input.staffCount,
    notes: input.notes,
  };
  return mutate(
    { entityType: 'payrollEvent', entityId: row.payrollId, action: 'create', after: row },
    async () => { await db.payrollEvents.put(row); return row; },
  );
}

export async function update(payrollId, patch) {
  const userId = await requireUserId();
  const existing = await db.payrollEvents.get(payrollId);
  if (!existing || existing.userId !== userId) throw new Error('payroll.update: not found');
  if (patch.payDate && !ISO_DATE_RE.test(patch.payDate)) throw new Error('payroll: payDate must be YYYY-MM-DD');
  const next = { ...existing, ...patch, amountCents: patch.amount != null ? cents(patch.amount) : existing.amountCents };
  return mutate(
    { entityType: 'payrollEvent', entityId: payrollId, action: 'update', before: existing, after: next },
    async () => { await db.payrollEvents.put(next); return next; },
  );
}

export async function remove(payrollId) {
  const userId = await requireUserId();
  const existing = await db.payrollEvents.get(payrollId);
  if (!existing || existing.userId !== userId) return;
  await mutate(
    { entityType: 'payrollEvent', entityId: payrollId, action: 'delete', before: existing },
    async () => { await db.payrollEvents.delete(payrollId); },
  );
}

/** Future payroll events from `from` (inclusive) to `to` (inclusive). */
export async function between(from, to) {
  const userId = await requireUserId();
  return db.payrollEvents
    .where('[userId+payDate]').between([userId, from], [userId, to], true, true)
    .toArray();
}

// ─── Check-number ranges ──────────────────────────────────────────────────

export async function getCheckRanges() {
  const c = await currentClaims();
  if (!c) return [];
  const p = await db.userProfile.get(c.sub);
  return (p && Array.isArray(p.payrollCheckRanges)) ? p.payrollCheckRanges : [];
}

/**
 * Replace the saved ranges. Each entry is `{from, to, label}`; missing or
 * negative ranges are dropped, and overlaps are kept (the matcher is OR-ed).
 */
export async function setCheckRanges(list) {
  const c = await currentClaims();
  if (!c) throw new Error('payroll: not signed in');
  const clean = (list || [])
    .map((r) => ({ from: Number(r.from) || 0, to: Number(r.to) || 0, label: String(r.label || '').slice(0, 60) }))
    .filter((r) => r.from > 0 && r.to >= r.from)
    .slice(0, 12);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  await db.userProfile.put({ ...existing, userId: c.sub, payrollCheckRanges: clean });
  return clean;
}

// Extract a check number from a bank transaction's description.
// Plaid descriptors that include checks usually look like "CHECK # 50123"
// or "CHECK 50123 ON …". The regex is intentionally narrow to avoid
// confusing card receipt numbers for check numbers.
const CHECK_RE = /\bCHECK\s*#?\s*(\d{2,8})\b/i;

export function checkNumberOf(tx) {
  if (!tx) return null;
  const hay = tx.description || tx.merchantName || '';
  const m = CHECK_RE.exec(hay);
  return m ? Number(m[1]) : null;
}

export function isPayrollCheck(tx, ranges) {
  if (!tx || (tx.amountCents || 0) <= 0) return false; // outflows only
  const n = checkNumberOf(tx);
  if (n == null) return false;
  return (ranges || []).some((r) => n >= r.from && n <= r.to);
}

/** Roll up payroll-check outflows by pay date. Newest-first. */
export function summarizeChecks(txs, ranges) {
  const checks = [];
  let total = 0;
  for (const tx of txs || []) {
    if (tx.pending) continue;
    if (!isPayrollCheck(tx, ranges)) continue;
    checks.push({ ...tx, _checkNumber: checkNumberOf(tx) });
    total += tx.amountCents || 0;
  }
  checks.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  return { checks, total };
}

// ─── ACH classifier — ADP-style descriptors ────────────────────────────────
//
// classifyPayrollTx looks at descriptors that don't carry a check number
// (direct deposits, fees, 401k transfers). Returns one of:
//   'run'      — payroll run (gross net deposits, an expense)
//   'fee'      — payroll-processing fee (expense)
//   '401k'     — retirement contribution transfer. In a small restaurant this is
//                almost always the OWNER's own retirement (employees rarely elect
//                a 401k — they'd open a personal Roth IRA). It is NOT a business
//                operating expense; cashflow counts it as money to the owner
//                (see cashflow/ownerDraw.js, ownerRetirementCents).
//   null       — not a payroll-related tx
//
// `taxKeywords` is the keyword set returned by cashflow.taxes.getKeywords(),
// used to suppress tax remittances (those belong on the Taxes report — we
// never want to double-count "ADP TAX" as both a tax payment and a payroll
// run).

const RUN_HINTS  = ['adp payroll', 'adp pay', 'adp wage', 'adp emp', 'payroll ach', 'gusto'];
const ADP_HINT   = 'adp';
const FEE_HINTS  = ['adp fee', 'adp fees', 'payroll fee', 'gusto fee'];
// Common providers a small-business owner funds a 401(k) through, plus generic
// markers. (Roth IRA is intentionally NOT here — that's a personal account funded
// outside the business, not a business cash outflow.)
const K401_HINTS = ['401k', '401(k)', 'adp 401', 'voya', 'fidelity 401', 'transamerica 401', 'principal 401', 'retirement plan', 'guideline', 'human interest', 'empower retirement', 'vestwell', 'ascensus'];

/**
 * True if a bank OUTFLOW looks like a 401(k) retirement contribution. The cashflow
 * owner-draw rollup uses this to count the owner's 401(k) as money to the owner
 * rather than a business operating expense.
 */
export function is401kContribution(tx, extraHints = []) {
  if (!tx || tx.pending) return false;
  if ((tx.amountCents || 0) <= 0) return false; // outflows only
  const hay = ((tx.merchantName || '') + ' ' + (tx.description || '')).toLowerCase();
  // K401_HINTS covers the common providers; `extraHints` are the operator's own
  // 401(k) transaction labels (their bank's descriptor) from settings.
  const hints = extraHints && extraHints.length
    ? K401_HINTS.concat(extraHints.map((h) => String(h).toLowerCase()).filter(Boolean))
    : K401_HINTS;
  return hints.some((k) => k && hay.includes(k));
}

export function classifyPayrollTx(tx, taxKeywords) {
  if (!tx || tx.pending) return null;
  if ((tx.amountCents || 0) <= 0) return null;
  // An explicit Spending categorization is authoritative: every pickable
  // category is non-payroll (Taxes, Software, …), so a stamped tx never
  // classifies as run/fee/401k. This is what lets an operator split a bare
  // "ADP" descriptor (IRS tax remittance → Taxes) from "ADP Payroll Fees"
  // (→ Software) — without it, bare "ADP" always counted as a payroll run.
  if (tx._spendCat) return null;
  const hay = ((tx.merchantName || '') + ' ' + (tx.description || '')).toLowerCase();
  // 401k first — "ADP 401k" must NOT classify as payroll run.
  if (K401_HINTS.some((k) => hay.includes(k))) return '401k';
  // Fees next, same reason.
  if (FEE_HINTS.some((k) => hay.includes(k))) return 'fee';
  // Tax suppression: any tax keyword wins over payroll-run. Use the same
  // leading word-boundary matcher as taxes.classifyTax so a TRACE# digit
  // run like "941" doesn't suppress real payroll-run hits.
  if (taxKeywords) {
    const allTaxKw = [
      ...(taxKeywords.payroll || []),
      ...(taxKeywords.excise || []),
      ...(taxKeywords.federal || []),
    ];
    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = (k) => {
      const key = String(k || '').toLowerCase();
      if (!key) return false;
      if (/[^\w ]/.test(key)) return hay.includes(key);
      return new RegExp('\\b' + escapeRegex(key), 'i').test(hay);
    };
    if (allTaxKw.some(matches)) return null;
  }
  if (RUN_HINTS.some((k) => hay.includes(k))) return 'run';
  // Bare "ADP" with no tax/401k/fee marker → assume payroll run.
  if (hay.includes(ADP_HINT)) return 'run';
  return null;
}

/**
 * Roll up ACH-based payroll-related activity (run / fee / 401k) over a tx
 * window. Returns separate arrays + totals; the Payroll report combines run
 * + fee with check-based payroll, and shows 401k as its own section — usually
 * the owner's retirement contribution (counted as money to the owner in
 * cashflow, not a business operating expense).
 */
export function summarizeAch(txs, taxKeywords) {
  const runs = [], fees = [], k401 = [];
  let runsCents = 0, feesCents = 0, k401Cents = 0;
  for (const tx of txs || []) {
    const k = classifyPayrollTx(tx, taxKeywords);
    if (k === 'run') { runs.push(tx); runsCents += tx.amountCents || 0; }
    else if (k === 'fee') { fees.push(tx); feesCents += tx.amountCents || 0; }
    else if (k === '401k') { k401.push(tx); k401Cents += tx.amountCents || 0; }
  }
  const byDate = (a, b) => (b.date || '').localeCompare(a.date || '');
  runs.sort(byDate); fees.sort(byDate); k401.sort(byDate);
  return { runs, fees, k401, runsCents, feesCents, k401Cents };
}
